import React, { useState } from 'react';
import { Table, Column } from './ui/Table';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
import { Button } from './ui/Button';
import { Search, Filter, Download, X } from 'lucide-react';
export interface Application {
  id: string;
  productId: string;
  merchantId: string;
  merchantName: string;
  loanAmount: number;
  loanTenor: number;
  status: 'Inactive' | 'Pending Approval' | 'Submitted to Checker' | 'Accepted' | 'Cooling Off Period' | 'Withdrawn' | 'Rejected' | 'Disbursement Ready' | 'Active' | 'Settled';
  mechanism: 'STP' | 'Validation';
  mechanismStage: 'Completed' | 'Maker' | 'Checker' | 'Approver';
  principalAmount: number;
  totalRepayment: number;
  interest: number;
  apr: number;
  fees: number;
  coolingOffTotal: number; // in hours
  coolingOffRemaining: number; // in hours
  merchantType: 'New' | 'Partner';
  partnerType: string;
  date: string;
}
interface ApplicationTableProps {
  applications: Application[];
  onApplicationClick: (app: Application) => void;
  title: string;
  filterStatus?: string;
}
export function ApplicationTable({
  applications,
  onApplicationClick,
  title,
  filterStatus
}: ApplicationTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(filterStatus || '');
  const [merchantTypeFilter, setMerchantTypeFilter] = useState('');
  // Filter logic
  const filteredApps = applications.filter(app => {
    const matchesSearch = app.id.toLowerCase().includes(searchQuery.toLowerCase()) || app.merchantName.toLowerCase().includes(searchQuery.toLowerCase()) || app.merchantId.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter ? app.status === statusFilter : true;
    const matchesMerchantType = merchantTypeFilter ? app.merchantType === merchantTypeFilter : true;
    return matchesSearch && matchesStatus && matchesMerchantType;
  });
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatCoolingOff = (total: number, remaining: number) => {
    if (total === 0) return 'N/A';
    return `${total}h | ${remaining}h left`;
  };
  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'Pending Approval':
        return 'bg-blue-100 text-blue-800';
      case 'Submitted to Checker':
        return 'bg-purple-100 text-purple-800';
      case 'Accepted':
        return 'bg-green-100 text-green-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      case 'Inactive':
        return 'bg-gray-100 text-gray-800';
      case 'Cooling Off Period':
        return 'bg-yellow-100 text-yellow-800';
      case 'Withdrawn':
        return 'bg-orange-100 text-orange-800';
      case 'Disbursement Ready':
        return 'bg-teal-100 text-teal-800';
      case 'Active':
        return 'bg-indigo-100 text-indigo-800';
      case 'Settled':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const columns: Column<Application>[] = [{
    key: 'id',
    label: 'Application ID'
  }, {
    key: 'productId',
    label: 'Product Code'
  }, {
    key: 'merchantId',
    label: 'Merchant ID'
  }, {
    key: 'merchantName',
    label: 'Merchant Name',
    render: item => <span className="font-medium text-gray-900" data-id="element-21">{item.merchantName}</span>
  }, {
    key: 'loanAmount',
    label: 'Loan Amount',
    render: item => <span className="font-semibold text-blue-600" data-id="element-22">
          {formatCurrency(item.loanAmount)}
        </span>
  }, {
    key: 'loanTenor',
    label: 'Tenor',
    render: item => `${item.loanTenor} mo`
  }, {
    key: 'status',
    label: 'Status',
    render: item => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${getStatusBadgeColor(item.status)}`} data-id="element-23">
          {item.status}
        </span>
  }, {
    key: 'mechanism',
    label: 'Mechanism'
  }, {
    key: 'mechanismStage',
    label: 'Stage',
    render: item => <span className="text-xs" data-id="element-24">{item.mechanismStage}</span>
  }, {
    key: 'principalAmount',
    label: 'Principal',
    render: item => formatCurrency(item.principalAmount)
  }, {
    key: 'totalRepayment',
    label: 'Total Repayment',
    render: item => formatCurrency(item.totalRepayment)
  }, {
    key: 'interest',
    label: 'Interest/Markup',
    render: item => formatCurrency(item.interest)
  }, {
    key: 'apr',
    label: 'APR',
    render: item => <span className="font-medium" data-id="element-25">{item.apr}%</span>
  }, {
    key: 'fees',
    label: 'Fees & Charges',
    render: item => formatCurrency(item.fees)
  }, {
    key: 'coolingOff',
    label: 'Cooling-Off',
    render: item => <span className="text-xs whitespace-nowrap" data-id="element-26">
          {formatCoolingOff(item.coolingOffTotal, item.coolingOffRemaining)}
        </span>
  }, {
    key: 'merchantType',
    label: 'Merchant Type'
  }, {
    key: 'partnerType',
    label: 'Partner Type'
  }];
  return <div className="space-y-6 animate-in fade-in duration-500" data-id="element-27">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-28">
        <div data-id="element-29">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-30">{title}</h1>
          <p className="text-gray-500 mt-1" data-id="element-31">
            Manage and track loan applications.
          </p>
        </div>
        <Button variant="secondary" data-id="element-32">
          <Download className="mr-2 h-4 w-4" data-id="element-33" />
          Export Data
        </Button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm space-y-4" data-id="element-34">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-35">
          <div className="relative col-span-1 md:col-span-2" data-id="element-36">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-37" />
            <Input placeholder="Search by App ID, Merchant Name..." className="pl-10" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-38" />
          </div>

          {!filterStatus && <Select options={[{
          value: 'Pending Approval',
          label: 'Pending Approval'
        }, {
          value: 'Submitted to Checker',
          label: 'Submitted to Checker'
        }, {
          value: 'Accepted',
          label: 'Accepted'
        }, {
          value: 'Rejected',
          label: 'Rejected'
        }, {
          value: 'Inactive',
          label: 'Inactive'
        }, {
          value: 'Cooling Off Period',
          label: 'Cooling Off Period'
        }, {
          value: 'Withdrawn',
          label: 'Withdrawn'
        }, {
          value: 'Disbursement Ready',
          label: 'Disbursement Ready'
        }, {
          value: 'Active',
          label: 'Active'
        }, {
          value: 'Settled',
          label: 'Settled'
        }]} value={statusFilter} onChange={e => setStatusFilter(e.target.value)} placeholder="Filter by Status" data-id="element-39" />}

          <Select options={[{
          value: 'New',
          label: 'New Merchant'
        }, {
          value: 'Partner',
          label: 'Partner Merchant'
        }]} value={merchantTypeFilter} onChange={e => setMerchantTypeFilter(e.target.value)} placeholder="Merchant Type" data-id="element-40" />
        </div>

        {(searchQuery || statusFilter || merchantTypeFilter) && <div className="flex items-center text-sm text-gray-500" data-id="element-41">
            <span className="mr-2" data-id="element-42">Active Filters:</span>
            {searchQuery && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md mr-2 flex items-center" data-id="element-43">
                Search: {searchQuery}{' '}
                <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setSearchQuery('')} data-id="element-44" />
              </span>}
            {statusFilter && !filterStatus && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md mr-2 flex items-center" data-id="element-45">
                Status: {statusFilter}{' '}
                <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setStatusFilter('')} data-id="element-46" />
              </span>}
            {merchantTypeFilter && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-md mr-2 flex items-center" data-id="element-47">
                Type: {merchantTypeFilter}{' '}
                <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => setMerchantTypeFilter('')} data-id="element-48" />
              </span>}
            <button className="text-blue-600 hover:text-blue-800 ml-auto" onClick={() => {
          setSearchQuery('');
          if (!filterStatus) setStatusFilter('');
          setMerchantTypeFilter('');
        }} data-id="element-49">
              Clear All
            </button>
          </div>}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-50">
        <div className="overflow-x-auto" data-id="element-51">
          <Table columns={columns} data={filteredApps} onRowClick={onApplicationClick} keyExtractor={item => item.id} data-id="element-52" />
        </div>
      </div>
    </div>;
}